import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { formatErrorResponse } from '../utils/errorHandlers'
import { stockItemAPI } from '../api/stockItemAPI';
import { stockItemCategoryAPI } from '../api/stockItemCategoryAPI'
import { vendorAPI } from '../api/vendorAPI'
import { countryAPI } from '../api/countryAPI'
import { itemFamilyAPI } from '../api/itemFamilyAPI'
import { brandAPI } from '../api/brandAPI'
import { settingsAPI } from '../api/settingsAPI'
import { convertTemporaryItemToStockItem } from '../api/poAPI';
import api from '../api/axiosConfig';
import { calculateNetCaseWeight, calculateGrossCaseWeight } from '../utils/commonUtils'
import Select from 'react-select'
import { usePermissions } from '../contexts/PermissionContext'


function StockItemDetailsScreen({ isModal = false, onStockItemCreated, onCancel, preselectedManufacturer }) {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()
    const { id } = useParams();
    const initialMode = location.state?.initialMode || (id ? 'update' : 'create');
    const mode = location.state?.update ? 'update' : initialMode;

    const [stockItem, setStockItem] = useState({
        sku: '',
        gtin: '',
        upc: '',
        description: '',
        categories: [],
        manufacturer: preselectedManufacturer || '',
        uom: 'bag',
        item_size: '',
        item_size_units: 'lbs',
        case_pack: '1',
        package_weight: '',
        package_weight_units: 'lbs',
        ti: '',
        hi: '',
        country_of_origin: '',
        dimensions_l: '',
        dimensions_w: '',
        dimensions_h: '',
        critical_stock_level: '',
        notes: '',
        image: null,
        is_active: true,
        item_family: '',
        is_manufactured: false,
        brand: ''
    })
    const [error, setError] = useState(null)
    const [availableCategories, setAvailableCategories] = useState([])
    const [availableVendors, setAvailableVendors] = useState([])
    const [availableCountries, setAvailableCountries] = useState([])
    const [availableItemFamilies, setAvailableItemFamilies] = useState([])
    const [availableBrands, setAvailableBrands] = useState([])
    const [netCaseWeight, setNetCaseWeight] = useState(0);
    const [grossCaseWeight, setGrossCaseWeight] = useState(0);
    const [initialStockItem, setInitialStockItem] = useState(null);
    const [systemSettings, setSystemSettings] = useState(null);

    const countryOptions = availableCountries.map(country => ({
        value: country.code,
        label: `${country.flag_emoji} ${country.name}`
    }));

    useEffect(() => {
        if (mode === 'update' && id) {
            console.log(`Fetching stock item with ID: ${id}`);
            const fetchStockItem = async () => {
                try {
                    const data = await stockItemAPI.getById(id);
                    console.log('Fetched stock item data:', data);
                    setStockItem(data);
                    setInitialStockItem(data);
                } catch (error) {
                    console.error('Error fetching stock item:', error);
                    setError(formatErrorResponse(error));
                }
            }
            fetchStockItem();
        } else {
            // Check if there's initial data from location state (for temporary item conversion)
            if (location.state?.initialData) {
                const initialData = location.state.initialData;
                setStockItem(prev => ({
                    ...prev,
                    description: initialData.description || prev.description,
                    manufacturer: initialData.manufacturer || prev.manufacturer
                }));
            }
            setInitialStockItem(stockItem);
        }
    }, [mode, id]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categories = await stockItemCategoryAPI.getAll()
                setAvailableCategories(categories)
            } catch (error) {
                setError(formatErrorResponse(error))
            }
        }
        fetchCategories()
    }, [])

    useEffect(() => {
        const fetchVendors = async () => {
            try {
                const vendors = await vendorAPI.getAll()
                setAvailableVendors(vendors)
            } catch (error) {
                setError(formatErrorResponse(error))
            }
        }
        fetchVendors()
    }, [])

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const countries = await countryAPI.getAll()
                setAvailableCountries(countries)
            } catch (error) {
                setError(formatErrorResponse(error))
            }
        }
        fetchCountries()
    }, [])

    useEffect(() => {
        const fetchItemFamilies = async () => {
            try {
                const response = await itemFamilyAPI.getAll(1, 1000, '')
                setAvailableItemFamilies(response.results || [])
            } catch (error) {
                console.error('Error fetching item families:', error)
            }
        }
        fetchItemFamilies()
    }, [])

    useEffect(() => {
        const fetchBrands = async () => {
            try {
                const response = await brandAPI.getAll(1, 1000, '', false)
                setAvailableBrands(response.results || [])
            } catch (error) {
                console.error('Error fetching brands:', error)
            }
        }
        fetchBrands()
    }, [])

    useEffect(() => {
        const fetchSystemSettings = async () => {
            try {
                const settings = await settingsAPI.get()
                setSystemSettings(settings)
            } catch (error) {
                console.error('Error fetching system settings:', error)
            }
        }
        fetchSystemSettings()
    }, [])

    useEffect(() => {
        const newNetCaseWeight = calculateNetCaseWeight(
            stockItem.item_size,
            stockItem.item_size_units,
            stockItem.case_pack
        );
        setNetCaseWeight(newNetCaseWeight);
    }, [stockItem.item_size, stockItem.item_size_units, stockItem.case_pack]);

    useEffect(() => {
        const newGrossCaseWeight = calculateGrossCaseWeight(
            stockItem.item_size,
            stockItem.item_size_units,
            stockItem.case_pack,
            stockItem.package_weight,
            stockItem.package_weight_units
        );
        setGrossCaseWeight(newGrossCaseWeight);
    }, [stockItem.item_size, stockItem.item_size_units, stockItem.case_pack, stockItem.package_weight, stockItem.package_weight_units]);

    // Handle scanned GTIN from barcode scanner
    useEffect(() => {
        if (location.state?.scannedGtin) {
            setStockItem(prev => ({ ...prev, gtin: location.state.scannedGtin }));
            // Clear the state after applying to prevent re-applying
            // Use setTimeout to avoid navigation during render
            const timer = setTimeout(() => {
                navigate(location.pathname + location.search, { replace: true, state: {} });
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [location.state?.scannedGtin, location.pathname, location.search, navigate]);

    // Handle scanned UPC from barcode scanner
    useEffect(() => {
        if (location.state?.scannedUpc) {
            setStockItem(prev => ({ ...prev, upc: location.state.scannedUpc }));
            // Clear the state after applying to prevent re-applying
            // Use setTimeout to avoid navigation during render
            const timer = setTimeout(() => {
                navigate(location.pathname + location.search, { replace: true, state: {} });
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [location.state?.scannedUpc, location.pathname, location.search, navigate]);

    const hasChanges = useCallback(() => {
        return JSON.stringify(stockItem) !== JSON.stringify(initialStockItem);
    }, [stockItem, initialStockItem]);

    const submitHandler = async (e) => {
        e.preventDefault()
        
        // Validate required fields
        if (!stockItem.country_of_origin) {
            setError('Country of Origin is required');
            return;
        }
        
        try {
            const formData = new FormData()
            
            Object.keys(stockItem).forEach(key => {
                if (key === 'image' && stockItem[key]) {
                    formData.append('image', stockItem[key])
                } else if (key === 'categories') {
                    stockItem.categories.forEach(categoryId => {
                        formData.append('categories', categoryId)
                    })
                } else if (key === 'item_family') {
                    // Always send item_family, even when null/empty (to allow clearing)
                    formData.append(key, stockItem[key] || '')
                } else if (key === 'brand') {
                    formData.append(key, stockItem[key] || '')
                } else if (key === 'notes') {
                    formData.append(key, stockItem[key] || '')
                } else if (stockItem[key]) {
                    formData.append(key, stockItem[key])
                }
            })
            
            if (mode === 'create') {
                // Check if this is a temporary item conversion
                if (location.state?.temporaryItemConversion) {
                    try {
                        const conversionData = location.state.temporaryItemConversion;
                        
                        if (!conversionData.po_line_id) {
                            throw new Error('Missing po_line_id in conversion data');
                        }
                        
                        const payload = {
                            po_line_id: conversionData.po_line_id,
                            stock_item_data: {
                                gtin: stockItem.gtin,
                                upc: stockItem.upc,
                                sku: stockItem.sku,
                                description: stockItem.description,
                                manufacturer: stockItem.manufacturer,
                                uom: stockItem.uom,
                                item_size: stockItem.item_size,
                                item_size_units: stockItem.item_size_units,
                                case_pack: stockItem.case_pack,
                                package_weight: stockItem.package_weight,
                                package_weight_units: stockItem.package_weight_units,
                                ti: stockItem.ti,
                                hi: stockItem.hi,
                                country_of_origin: stockItem.country_of_origin,
                                dimensions_l: stockItem.dimensions_l,
                                dimensions_w: stockItem.dimensions_w,
                                dimensions_h: stockItem.dimensions_h,
                                critical_stock_level: stockItem.critical_stock_level,
                                notes: stockItem.notes,
                                categories: stockItem.categories
                            },
                            vendor_item_id: conversionData.vendor_item_id || '',
                            price: conversionData.price
                        };
                        
                        await convertTemporaryItemToStockItem(payload);
                        const returnPath = location.state.returnTo || '/stock-items';
                        navigate(returnPath, {
                            state: { 
                                refreshAfterConversion: true,
                                update: true  // Preserve editing mode
                            }
                        });
                    } catch (conversionError) {
                        console.error('Error converting temporary item:', conversionError);
                        setError('Conversion failed: ' + formatErrorResponse(conversionError));
                        // Don't navigate, stay on the page so user can see the error and fix it
                    }
                } else {
                    // Normal stock item creation (not a conversion)
                    console.log('Creating new stock item');
                    const response = await stockItemAPI.create(formData)
                    if (isModal && onStockItemCreated) {
                        onStockItemCreated(response);
                    } else {
                        navigate('/stock-items')
                    }
                }
            } else if (mode === 'update' && id) {
                console.log(`Updating stock item with ID: ${id}`);
                await stockItemAPI.update(id, formData)
                navigate('/stock-items')
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setError(formatErrorResponse(error))
        }
    }

    const toggleCategory = (categoryId) => {
        setStockItem(prevItem => {
            const numericCategoryId = Number(categoryId);
            const isCategorySelected = prevItem.categories.includes(numericCategoryId);
            const updatedCategories = isCategorySelected
                ? prevItem.categories.filter(id => id !== numericCategoryId)
                : [...prevItem.categories, numericCategoryId];
            return { ...prevItem, categories: updatedCategories };
        });
    }

    const handleImageUpload = (e) => {
        const file = e.target.files[0]
        if (file) {
            setStockItem({...stockItem, image: file})
        }
    }

    const handleDragOver = (e) => {
        e.preventDefault()
    }

    const handleDrop = (e) => {
        e.preventDefault()
        const file = e.dataTransfer.files[0]
        if (file) {
            setStockItem({...stockItem, image: file})
        }
    }

    // Function to get the full image URL
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        // If it's already a full URL, return as is
        if (imagePath.startsWith('http')) return imagePath;
        // Otherwise, prepend the API base URL
        return `${api.defaults.baseURL}${imagePath}`;
    };

    if (!isModal && !permissionsLoading && !hasPermission('stock.items.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to {mode === 'create' ? 'create' : 'update'} stock items.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col md={isModal ? 12 : 8} lg={isModal ? 12 : 6}>
                    {!isModal && <h1>{mode === 'create' ? 'Create Stock Item' : 'Update Stock Item'}</h1>}
                    
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="sku">
                            <Form.Label>SKU</Form.Label>
                            <Form.Control
                                type="text"
                                value={stockItem.sku}
                                onChange={(e) => setStockItem({...stockItem, sku: e.target.value})}
                                placeholder="Enter SKU"
                                style={{ height: '38px' }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>
                        <Form.Group className="mb-4" controlId="gtin">
                            <Form.Label>GTIN*</Form.Label>
                            <div className="d-flex">
                                <Form.Control
                                    type="text"
                                    value={stockItem.gtin}
                                    onChange={(e) => setStockItem({...stockItem, gtin: e.target.value})}
                                    required
                                    placeholder="Enter GTIN"
                                    style={{ height: '38px' }}
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Scan GTIN Barcode</Tooltip>}
                                >
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            navigate('/scan-barcode', {
                                                state: {
                                                    mode: 'gtin',
                                                    returnPath: location.pathname + location.search
                                                }
                                            });
                                        }}
                                        style={{ marginLeft: '5px', height: '38px' }}
                                    >
                                        <i className="bi bi-upc-scan"></i>
                                    </Button>
                                </OverlayTrigger>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Print GTIN Barcode</Tooltip>}
                                >
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            if (stockItem.gtin) {
                                                navigate(`/print-barcode?gtin=${stockItem.gtin}`);
                                            }
                                        }}
                                        disabled={!stockItem.gtin}
                                        style={{ marginLeft: '5px', height: '38px' }}
                                    >
                                        <i className="bi bi-printer"></i>
                                    </Button>
                                </OverlayTrigger>
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="upc">
                            <Form.Label>UPC</Form.Label>
                            <div className="d-flex">
                                <Form.Control
                                    type="text"
                                    value={stockItem.upc}
                                    onChange={(e) => setStockItem({...stockItem, upc: e.target.value})}
                                    placeholder="Enter UPC"
                                    style={{ height: '38px' }}
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Scan UPC Barcode</Tooltip>}
                                >
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            navigate('/scan-barcode', {
                                                state: {
                                                    mode: 'upc',
                                                    returnPath: location.pathname + location.search
                                                }
                                            });
                                        }}
                                        style={{ marginLeft: '5px', height: '38px' }}
                                    >
                                        <i className="bi bi-upc-scan"></i>
                                    </Button>
                                </OverlayTrigger>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Print UPC Barcode</Tooltip>}
                                >
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            if (stockItem.upc) {
                                                navigate(`/print-barcode?upc=${stockItem.upc}`);
                                            }
                                        }}
                                        disabled={!stockItem.upc}
                                        style={{ marginLeft: '5px', height: '38px' }}
                                    >
                                        <i className="bi bi-printer"></i>
                                    </Button>
                                </OverlayTrigger>
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="description">
                            <Form.Label>Description*</Form.Label>
                            <Form.Control
                                type="text"
                                value={stockItem.description}
                                onChange={(e) => setStockItem({...stockItem, description: e.target.value})}
                                required
                                placeholder="Enter Description"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label>Product Image</Form.Label>
                            <div
                                className="border rounded p-3 text-center"
                                style={{ 
                                    cursor: 'pointer',
                                    backgroundColor: 'var(--bs-secondary-bg)',
                                    border: '2px dashed var(--bs-border-color)',
                                    minHeight: '150px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--bs-tertiary-bg)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--bs-secondary-bg)';
                                }}
                                onFocus={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--bs-tertiary-bg)';
                                }}
                                onBlur={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--bs-secondary-bg)';
                                }}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() => document.getElementById('imageInput').click()}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        document.getElementById('imageInput').click();
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                            >
                                {stockItem.image ? (
                                    <div>
                                        <img 
                                            src={stockItem.image instanceof File ? URL.createObjectURL(stockItem.image) : getImageUrl(stockItem.image)} 
                                            alt="Preview" 
                                            style={{ maxHeight: '120px', maxWidth: '100%', marginBottom: '10px' }} 
                                        />
                                        <div>Drag and drop an image here or click to browse</div>
                                    </div>
                                ) : (
                                    <>
                                        <i className="fas fa-cloud-upload-alt mb-2" style={{ fontSize: '2rem' }}></i>
                                        <div>Drag and drop an image here or click to browse</div>
                                    </>
                                )}
                                <Form.Control
                                    id="imageInput"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    style={{ display: 'none' }}
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                            </div>
                            <div className="d-flex justify-content-start mt-2">
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setStockItem({...stockItem, image: null});
                                    }}
                                >
                                    Clear Image
                                </Button>
                            </div>
                        </Form.Group>

                       <Form.Group className="mb-4" controlId="categories">
                            <Form.Label>Categories</Form.Label>
                            <div className="d-flex flex-wrap gap-2 p-3 border rounded">
                                {availableCategories.map((category) => (
                                    <Badge
                                        key={category._id}
                                        bg={stockItem.categories.includes(category._id) ? "success" : "light"}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => toggleCategory(category._id)}
                                    >
                                        {category.name}
                                    </Badge>
                                ))}
                            </div>
                            <div className="d-flex justify-content-start mt-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setStockItem({...stockItem, categories: []})}
                                >
                                    Clear All
                                </Button>
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="item_family">
                            <Form.Label>Item Family</Form.Label>
                            <Form.Select
                                value={stockItem.item_family || ''}
                                onChange={(e) => setStockItem({...stockItem, item_family: e.target.value ? parseInt(e.target.value) : null})}
                            >
                                <option value="">No Family</option>
                                {availableItemFamilies.map((family) => (
                                    <option key={family._id} value={family._id}>
                                        {family.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Group this item with other interchangeable items for recipe substitution
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="is_manufactured">
                            <Form.Check
                                type="checkbox"
                                label="Manufactured In-House"
                                checked={stockItem.is_manufactured || false}
                                onChange={(e) => {
                                    const isChecked = e.target.checked
                                    if (isChecked && systemSettings?.default_manufacturer) {
                                        setStockItem({
                                            ...stockItem,
                                            is_manufactured: true,
                                            manufacturer: systemSettings.default_manufacturer
                                        })
                                    } else {
                                        setStockItem({...stockItem, is_manufactured: isChecked})
                                    }
                                }}
                            />
                            <Form.Text className="text-muted">
                                Check if this item is produced in-house rather than purchased
                                {systemSettings?.default_manufacturer_name && (
                                    <span> (will set manufacturer to {systemSettings.default_manufacturer_name})</span>
                                )}
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="manufacturer">
                            <Form.Label>Manufacturer*</Form.Label>
                            <Form.Select
                                value={stockItem.manufacturer}
                                onChange={(e) => setStockItem({...stockItem, manufacturer: e.target.value})}
                                required
                            >
                                <option value="">Select a Manufacturer</option>
                                {availableVendors.map((vendor) => (
                                    <option key={vendor._id} value={vendor._id}>
                                        {vendor.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {stockItem.is_manufactured && (
                            <Form.Group className="mb-4" controlId="brand">
                                <Form.Label>Brand</Form.Label>
                                <Form.Select
                                    value={stockItem.brand || ''}
                                    onChange={(e) => setStockItem({...stockItem, brand: e.target.value ? parseInt(e.target.value) : null})}
                                >
                                    <option value="">No Brand</option>
                                    {availableBrands.map((brand) => (
                                        <option key={brand._id} value={brand._id}>
                                            {brand.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}

                        <Form.Group className="mb-4" controlId="uom">
                            <Form.Label>Units of Measure*</Form.Label>
                            <Form.Select
                                value={stockItem.uom}
                                onChange={(e) => setStockItem({...stockItem, uom: e.target.value})}
                                required
                            >
                                <option value="bag">Bag</option>
                                <option value="bulk">Bulk</option>
                                <option value="clear_film">Clear Film</option>
                                <option value="ct">Count</option>
                                <option value="film">Film</option>
                                <option value="jar">Jar</option>
                                <option value="pallet">Pallet</option>
                                <option value="tub">Tub</option>
                                <option value="tote_bag">Tote Bag</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="item_size">
                            <Form.Label>Item Size</Form.Label>
                            <div className="d-flex">
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={stockItem.item_size}
                                    onChange={(e) => setStockItem({...stockItem, item_size: e.target.value})}
                                    placeholder="Enter Item Size"
                                    className="me-2"
                                    min="0"
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                <Form.Select
                                 style={{ width: '80px' }}
                                    value={stockItem.item_size_units}
                                    onChange={(e) => setStockItem({...stockItem, item_size_units: e.target.value})}
                                >
                                    <option value="lbs">lbs</option>
                                    <option value="oz">oz</option>
                                </Form.Select>
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="case_pack">
                            <Form.Label>Case Pack*</Form.Label>
                            <Form.Control
                                type="number"
                                value={stockItem.case_pack}
                                onChange={(e) => setStockItem({...stockItem, case_pack: e.target.value})}
                                placeholder="Enter Case Pack"
                                required
                                min="0"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="net_case_weight">
                            <Form.Label>Net Case Weight (lbs)</Form.Label>
                            <Form.Control
                                type="number"
                                value={netCaseWeight}
                                readOnly
                                placeholder="Net Case Weight"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>                        

                        <Form.Group className="mb-4" controlId="package_weight">
                            <Form.Label>Package Weight</Form.Label>
                            <div className="d-flex">
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={stockItem.package_weight}
                                    onChange={(e) => setStockItem({...stockItem, package_weight: e.target.value})}
                                    placeholder="Enter Package Weight"
                                    className="me-2"
                                    min="0"
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                <Form.Select
                                 style={{ width: '80px' }}
                                    value={stockItem.package_weight_units}
                                    onChange={(e) => setStockItem({...stockItem, package_weight_units: e.target.value})}
                                >
                                    <option value="lbs">lbs</option>
                                    <option value="oz">oz</option>
                                </Form.Select>
                            </div>
                        </Form.Group>


                        <Form.Group className="mb-4" controlId="gross_case_weight">
                            <Form.Label>Gross Case Weight</Form.Label>
                            <Form.Control
                                type="number"
                                value={grossCaseWeight}
                                readOnly
                                placeholder="Gross Case Weight"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group> 

                        <Form.Group className="mb-4" controlId="tier">
                            <Form.Label>TI</Form.Label>
                            <Form.Control
                                type="number"
                                step="1"
                                value={stockItem.ti}
                                onChange={(e) => setStockItem({...stockItem, ti: e.target.value})}
                                placeholder="Enter Tier"
                                min="0"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="height">
                            <Form.Label>HI</Form.Label>
                            <Form.Control
                                type="number"
                                step="1"
                                min="0"
                                value={stockItem.hi}
                                onChange={(e) => setStockItem({...stockItem, hi: e.target.value})}
                                placeholder="Enter Tier Height"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="country_of_origin">
                            <Form.Label>Country of Origin*</Form.Label>
                            <Select
                                value={countryOptions.find(option => option.value === stockItem.country_of_origin)}
                                onChange={(selectedOption) => setStockItem({
                                    ...stockItem,
                                    country_of_origin: selectedOption ? selectedOption.value : ''
                                })}
                                options={countryOptions}
                                placeholder="Select a Country of Origin"
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="dimensions_l">
                            <Form.Label>Length (inch)</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                value={stockItem.dimensions_l}
                                onChange={(e) => setStockItem({...stockItem, dimensions_l: e.target.value})}
                                placeholder="Enter Length (inch)"
                                min="0"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="dimensions_w">
                            <Form.Label>Width (inch)</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                value={stockItem.dimensions_w}
                                onChange={(e) => setStockItem({...stockItem, dimensions_w: e.target.value})}
                                placeholder="Enter Width (inch)"
                                min="0"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="dimensions_h">
                            <Form.Label>Height (inch)</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                value={stockItem.dimensions_h}
                                onChange={(e) => setStockItem({...stockItem, dimensions_h: e.target.value})}
                                placeholder="Enter Height (inch)"
                                min="0"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="critical_stock_level">
                            <Form.Label>
                                Critical Stock Level
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip>
                                            Minimum stock level threshold. When total inventory falls below this level, 
                                            the item will appear in &apos;Almost Out of Stock&apos; reports. Leave empty to disable alerts.
                                        </Tooltip>
                                    }
                                >
                                    <span className="ms-1 text-info">
                                        <i className="bi bi-info-circle"></i>
                                    </span>
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type="number"
                                value={stockItem.critical_stock_level}
                                onChange={(e) => setStockItem({...stockItem, critical_stock_level: e.target.value})}
                                placeholder="Enter minimum stock level (optional)"
                                min="0"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="notes">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                value={stockItem.notes}
                                onChange={(e) => setStockItem({...stockItem, notes: e.target.value})}
                                placeholder="Enter additional notes or information about this stock item (optional)"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>
                        {error && (
                        <Alert variant="danger" onClose={() => setError(null)} dismissible>
                            {error}
                            </Alert>
                        )}
                        <Button 
                            variant="success" 
                            type="submit" 
                            className="me-2"
                            disabled={mode === 'update' && !hasChanges()}
                        >
                            {mode === 'create' ? 'Create' : 'Update'}
                        </Button>
                        <Button 
                            variant="outline-primary" 
                            onClick={isModal ? onCancel : () => navigate('/stock-items')}
                        >
                            <i className="fas fa-arrow-left me-2"></i>
                            Back to List
                        </Button>
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default StockItemDetailsScreen 